import { useCallback, useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useOrganization } from '@/contexts/OrganizationContext'
import { createTenantDb } from '@/lib/db-tenant'
import type { BulkWriteOutcome } from '@/lib/projection-cell-db'
import { invalidateAfterProjectionWrite } from '@/lib/projection-invalidation'
import { PROJECTION_KEYS } from '@/lib/query-keys'
import { cellFacilityIds, type ProjectionCellScope, type ProjectionRecord } from '@/lib/projection-cell'

/** Stable empty list while loading, so consumers memoizing on `rows` do not churn. */
const NO_ROWS: ProjectionRecord[] = []

/**
 * The projections of one Cash Position cell, and `settle`, which resolves with
 * the rows once they reflect the latest write: the write's invalidation already
 * started the refetch, and `cancelRefetch: false` joins it. The rows come from
 * the cache, not the caller's render, which still holds the pre-write list.
 * `categoryIds` is `null` while categories load.
 */
export function useProjectionCellRows(scope: ProjectionCellScope, categoryIds: readonly string[] | null) {
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganization()
  const orgId = currentOrganization?.id
  const { target, start, end } = scope
  const facilityIds = useMemo(() => cellFacilityIds(target), [target])
  const queryKey = useMemo(
    () => PROJECTION_KEYS.cell(orgId, facilityIds, start, end, categoryIds ?? []),
    [orgId, facilityIds, start, end, categoryIds],
  )

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      if (!orgId) throw new Error('No organization selected')
      const { data, error } = await createTenantDb(orgId).projectionCell.list({
        facilityIds,
        start,
        end,
        categoryIds: categoryIds ?? [],
      })
      if (error) throw new Error(error.message)
      return data
    },
    enabled: !!orgId && categoryIds !== null,
  })

  const settle = useCallback(async (): Promise<ProjectionRecord[]> => {
    await queryClient.refetchQueries({ queryKey, exact: true }, { cancelRefetch: false })
    return queryClient.getQueryData<ProjectionRecord[]>(queryKey) ?? NO_ROWS
  }, [queryClient, queryKey])

  return { ...query, rows: query.data ?? NO_ROWS, settle }
}

/** Rejects only when a bulk write failed without writing anything; partial results resolve. */
function requireSomeWritten(outcome: BulkWriteOutcome): BulkWriteOutcome {
  if (outcome.updated === 0 && outcome.firstError) throw new Error(outcome.firstError.message)
  return outcome
}

/** Sets one date on several projections. */
export function useBulkSetProjectionDate() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganization()

  return useMutation({
    mutationFn: async ({ ids, date }: { ids: readonly string[]; date: string }) => {
      if (!currentOrganization?.id) throw new Error('No organization selected')
      return requireSomeWritten(await createTenantDb(currentOrganization.id).projectionCell.setDate(ids, date))
    },
    // Also after a failure: a rejected batch may have lost a race with an edit elsewhere.
    onSettled: () => {
      invalidateAfterProjectionWrite(queryClient)
    },
  })
}

/** Moves several projections one calendar day later. */
export function useBulkMoveProjectionsToNextDay() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganization()

  return useMutation({
    mutationFn: async (rows: ReadonlyArray<Pick<ProjectionRecord, 'id' | 'date'>>) => {
      if (!currentOrganization?.id) throw new Error('No organization selected')
      return requireSomeWritten(await createTenantDb(currentOrganization.id).projectionCell.moveToNextDay(rows))
    },
    // Also after a failure: a rejected batch may have lost a race with an edit elsewhere.
    onSettled: () => {
      invalidateAfterProjectionWrite(queryClient)
    },
  })
}
